import { BigNumber } from '@ethersproject/bignumber';
import cn from 'classnames';
import { motion } from 'framer-motion';
import React, { memo, useCallback, useEffect, useState } from 'react';

import { useThemeColors } from '@/shared/theme';

import { humanizeBn } from 'tools/math';
import { addressShortView } from 'utils';

interface IWalletItemProps {
  address: string;
  balance: string;
  setBalance: (b: string, adr: string) => any;
  walletBalance: BigNumber;
  decimals: number;
  type: 'deposit' | 'withdraw';
  distributionTouched: boolean;
  disabled: boolean;
  isValid: boolean;
}

const WalletItemComponent: React.FC<IWalletItemProps> = ({
  address,
  balance,
  setBalance,
  walletBalance,
  decimals,
  type,
  distributionTouched,
  disabled,
  isValid,
}) => {
  const palette = useThemeColors();
  const [walletTouched, setWalletTouched] = useState<boolean>(distributionTouched);

  useEffect(() => {
    if (distributionTouched) {
      setWalletTouched(true);
    }
  }, [distributionTouched]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.currentTarget.value;

      if (newValue.split('.')[1]?.length > decimals) return;

      setWalletTouched(true);
      setBalance(isNaN(Number(newValue)) ? '' : newValue, address);
    },
    [decimals, address, setBalance],
  );

  const handleSetMaxValue = useCallback(() => {
    setBalance(humanizeBn(walletBalance, decimals), address);
  }, [setBalance, walletBalance, decimals, address]);

  return (
    <li>
      <div className="wallet-hint">
        <span className="wallet-balance">Balance: {humanizeBn(walletBalance, decimals)}</span>
        {!isValid && walletTouched && !disabled && (
          <span className="wallet-error">Invalid amount</span>
        )}
      </div>
      <div className={cn('wallet-item', { _disabled: !!disabled })}>
        <span className="wallet-address">{addressShortView(address)}</span>
        <div className="wallet-item__right">
          {!disabled && (
            <input type="text" disabled={disabled} value={balance} onChange={handleChange} />
          )}
          {type === 'withdraw' && !disabled && (
            <motion.button
              type="button"
              className="max-button without-padding"
              whileTap={{ backgroundColor: palette.light_gray_1 }}
              onClick={handleSetMaxValue}
            >
              Max
            </motion.button>
          )}
        </div>
      </div>
    </li>
  );
};

const WalletItem = memo(WalletItemComponent);

export { WalletItem };
